'use client';

import { useCareToday } from '@/hooks/useCareToday';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  LifeBuoy,
  BookOpen,
  Minus,
  GripHorizontal,
  Info,
} from 'lucide-react';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { useAuthStore } from '../stores/auth-store';
import { useHelpTenantContext } from '@/hooks/useHelpTenantContext';
import { useOrgLabels } from '@/contexts/org-labels-context';
import { getFeatureFlags, isFeatureEnabled } from '@/lib/supabase/feature-flags';
import { UNIFIED_INCIDENT_CAPTURE_FLAG } from '@/lib/incidents/unified-capture';
import {
  HELP_CHAT_RATE_LIMIT_REPLY,
  HELP_CHAT_TRANSPORT_ERROR_REPLY,
  SENSITIVE_HELP_CHAT_ERROR,
} from '@/lib/help/help-chat-guard';
import { HELP_CHAT_MAX_MESSAGES } from '@/lib/help/help-chat-limits';
import {
  getHelpChatFollowUpQuestions,
  getHelpChatQuestionForPath,
  getHelpChatSuggestions,
} from '@/lib/help/help-suggestions';
import { normalizeHelpChatPath } from '@/lib/help/help-path';
import { useStationPinHelpCapability } from '@/lib/help/station-pin-help-capability';
import { formatEnum } from '@/lib/format';
import { isHelpChatTicketEligible } from '@/lib/help/help-ticket-contract';
import { HelpTicketQuietExits } from '@/components/help/help-ticket-quiet-exits';
import type { ProgramFamily } from '@statura/experience-contract';
import type { ProviderProgramContext, ProviderType } from '@/lib/navigation/provider-type';
import type { GovernmentIntegrationEnablement } from '@/lib/integrations/government/enablement-types';
import { useModalLayer } from '@statura/ui';

interface HelpChatArticleLink {
  id: string;
  title: string;
  summary: string;
  href: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  articles?: HelpChatArticleLink[];
  evidence?: AssistantEvidenceCard[];
  resultCount?: number;
  resolution?: 'saving' | 'confirmed_complete' | 'not_resolved';
  /** Signed server scope for feedback on this exact assistant turn. */
  outcomeBinding?: string;
}

interface HelpChatWidgetProps {
  userName: string;
  activeProgramFamily: ProgramFamily | null;
  providerType: ProviderType;
  programContextRevision: string;
  assistantConversationEnabled?: boolean;
  assistantProactiveEnabled?: boolean;
}

interface StoredPosition {
  x: number;
  y: number;
}

// Panel physical dimensions — used for clamping and snap presets.
const PANEL_WIDTH = 384; // w-96
const PANEL_MAX_HEIGHT = 600;
const MIN_PANEL_HEIGHT = 280;
const EDGE_MARGIN = 16;
const MOBILE_HELP_MEDIA_QUERY = '(max-width: 767px)';
const HELP_CHAT_DIALOG_TITLE_ID = 'help-chat-dialog-title';
const HELP_CHAT_DIALOG_DESC_ID = 'help-chat-dialog-description';

function renderedLayerHeight(element: HTMLElement): number {
  let height = 0;
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    const arbitraryZ = node.className.toString().match(/(?:^|\s)z-\[(\d+)\](?:\s|$)/)?.[1];
    height = Math.max(
      height,
      Number.parseInt(window.getComputedStyle(node).zIndex, 10) || 0,
      arbitraryZ ? Number.parseInt(arbitraryZ, 10) : 0,
    );
  }
  return height;
}

function isVisibleLayer(element: HTMLElement): boolean {
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (
      node.hidden ||
      node.hasAttribute('inert') ||
      node.getAttribute('aria-hidden') === 'true' ||
      style.display === 'none' ||
      style.visibility === 'hidden'
    )
      return false;
  }
  return true;
}

/** Match the canonical D27 rendered-layer selectors before moving focus. */
function isTopRenderedLayer(panel: HTMLElement): boolean {
  const layers = Array.from(
    panel.ownerDocument.querySelectorAll<HTMLElement>(
      '[data-dispatch-layer], [data-popover-layer], [role="dialog"], [data-modal-layer], [data-help-layer]',
    ),
  ).filter(isVisibleLayer);
  const top = layers.reduce<HTMLElement | null>((highest, candidate) => {
    if (!highest) return candidate;
    const candidateHeight = renderedLayerHeight(candidate);
    const highestHeight = renderedLayerHeight(highest);
    if (candidateHeight !== highestHeight)
      return candidateHeight > highestHeight ? candidate : highest;
    return highest.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING
      ? candidate
      : highest;
  }, null);
  return top === panel;
}

// Default position — bottom-right, sized to fit the opened chat panel.
function getDefaultPosition(): StoredPosition {
  if (typeof window === 'undefined') return { x: 0, y: 0 };
  const w = getPanelWidth();
  const h = getPanelHeight();
  return {
    x: window.innerWidth - w - EDGE_MARGIN,
    y: window.innerHeight - h - EDGE_MARGIN,
  };
}

function getPanelWidth(): number {
  if (typeof window === 'undefined') return PANEL_WIDTH;
  return Math.max(0, Math.min(PANEL_WIDTH, window.innerWidth - EDGE_MARGIN * 2));
}

function latestUserQuestion(messages: readonly ChatMessage[]): string {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role === 'user') return message.content;
  }
  return '';
}

const ASSISTANT_TICKET_PREVIEW_LABELS = {
  type_of_help: 'Type of help',
  request: 'Request',
  what_happened: 'What happened',
  what_should_have_happened: 'What should have happened',
  what_tried: 'What they tried',
  task_checked: 'Task checked',
  related_task: 'Related task',
  suggested_effect: 'Suggested effect on work',
  will_go_to: 'Will go to',
} as const;
const ASSISTANT_TICKET_CATEGORIES = new Set([
  'HOW_TO',
  'BUG_REPORT',
  'FEATURE_REQUEST',
  'DATA_ISSUE',
  'ACCESS_ISSUE',
  'PLATFORM_ISSUE',
  'BILLING_ISSUE',
  'OTHER',
]);
type AssistantTicketPreviewKey = keyof typeof ASSISTANT_TICKET_PREVIEW_LABELS;
type AssistantTicketPreviewRow = {
  key: AssistantTicketPreviewKey;
  label: (typeof ASSISTANT_TICKET_PREVIEW_LABELS)[AssistantTicketPreviewKey];
  value: string;
};
type AssistantTicketDraftPreview = {
  primary: AssistantTicketPreviewRow[];
  submittedDescription: string;
};
type AssistantTicketPreview = {
  token: string;
  category: string;
  subject: string;
  description: string;
  expiresAt: number;
  routeTarget?: 'tenant_administrators' | 'statura_support';
  preview?: AssistantTicketDraftPreview;
};

function parseAssistantTicketDraft(value: unknown): AssistantTicketPreview | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Record<string, unknown>;
  if (
    typeof candidate.token !== 'string' ||
    candidate.token.length === 0 ||
    candidate.token.length > 4_000 ||
    typeof candidate.category !== 'string' ||
    !ASSISTANT_TICKET_CATEGORIES.has(candidate.category) ||
    typeof candidate.subject !== 'string' ||
    candidate.subject.length === 0 ||
    candidate.subject.length > 140 ||
    typeof candidate.description !== 'string' ||
    candidate.description.length === 0 ||
    candidate.description.length > 1_500 ||
    typeof candidate.expiresAt !== 'number' ||
    !Number.isFinite(candidate.expiresAt) ||
    (candidate.routeTarget !== undefined &&
      candidate.routeTarget !== 'tenant_administrators' &&
      candidate.routeTarget !== 'statura_support')
  )
    return null;

  let preview: AssistantTicketDraftPreview | undefined;
  if (candidate.preview && typeof candidate.preview === 'object') {
    const rawPreview = candidate.preview as Record<string, unknown>;
    const rawRows = rawPreview.primary;
    const seen = new Set<string>();
    if (
      Array.isArray(rawRows) &&
      rawRows.length > 0 &&
      rawRows.length <= 8 &&
      rawPreview.submittedDescription === candidate.description
    ) {
      const primary = rawRows.flatMap((rawRow): AssistantTicketPreviewRow[] => {
        if (!rawRow || typeof rawRow !== 'object') return [];
        const row = rawRow as Record<string, unknown>;
        if (
          typeof row.key !== 'string' ||
          !Object.hasOwn(ASSISTANT_TICKET_PREVIEW_LABELS, row.key) ||
          row.label !== ASSISTANT_TICKET_PREVIEW_LABELS[row.key as AssistantTicketPreviewKey] ||
          typeof row.value !== 'string' ||
          row.value.length === 0 ||
          row.value.length > 400 ||
          row.value.trim() !== row.value ||
          seen.has(row.key)
        )
          return [];
        seen.add(row.key);
        return [row as AssistantTicketPreviewRow];
      });
      const hasRequiredRows =
        seen.has('type_of_help') && seen.has('suggested_effect') && seen.has('will_go_to');
      const taskRows = Number(seen.has('task_checked')) + Number(seen.has('related_task'));
      if (primary.length === rawRows.length && hasRequiredRows && taskRows <= 1) {
        preview = { primary, submittedDescription: candidate.description };
      }
    }
  }

  return {
    token: candidate.token,
    category: candidate.category,
    subject: candidate.subject,
    description: candidate.description,
    expiresAt: candidate.expiresAt,
    routeTarget: candidate.routeTarget as AssistantTicketPreview['routeTarget'],
    preview,
  };
}

type AssistantHelpOffer = {
  handle: string;
  task: 'help_navigation' | 'roster_draft_save' | 'invitation_delivery';
  message: string;
};

type AssistantEvidenceCard =
  | {
      kind: 'technical_status';
      task: 'help_navigation' | 'roster_draft_save' | 'invitation_delivery';
      outcome: 'succeeded' | 'failed' | 'unknown';
      code: string | null;
      observedAt: string;
      originProgramme?: 'SAH' | 'RAC' | 'CHSP' | 'SHARED';
    }
  | {
      kind: 'known_issue';
      task: 'help_navigation' | 'roster_draft_save' | 'invitation_delivery';
      noticeCode: string;
    }
  | {
      kind: 'ticket_status';
      status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED' | 'UNKNOWN';
      category: string;
      priority: string;
      notificationState: 'pending' | 'processing' | 'delivered' | 'dead' | 'UNKNOWN';
      updatedAt: string | null;
    };

function parseAssistantEvidenceCard(value: unknown): AssistantEvidenceCard | null {
  if (!value || typeof value !== 'object') return null;
  const card = value as Record<string, unknown>;
  if (card.kind === 'technical_status') {
    const tasks = ['help_navigation', 'roster_draft_save', 'invitation_delivery'] as const;
    const outcomes = ['succeeded', 'failed', 'unknown'] as const;
    const programmes = ['SAH', 'RAC', 'CHSP', 'SHARED'] as const;
    if (
      !tasks.includes(card.task as (typeof tasks)[number]) ||
      !outcomes.includes(card.outcome as (typeof outcomes)[number]) ||
      (card.code !== null &&
        (typeof card.code !== 'string' || !/^[A-Z][A-Z0-9_]{2,80}$/.test(card.code))) ||
      typeof card.observedAt !== 'string' ||
      !Number.isFinite(Date.parse(card.observedAt)) ||
      (card.originProgramme !== undefined &&
        !programmes.includes(card.originProgramme as (typeof programmes)[number]))
    )
      return null;
    return card as AssistantEvidenceCard;
  }
  if (card.kind === 'known_issue') {
    const tasks = ['help_navigation', 'roster_draft_save', 'invitation_delivery'] as const;
    if (
      !tasks.includes(card.task as (typeof tasks)[number]) ||
      typeof card.noticeCode !== 'string' ||
      !/^[A-Z][A-Z0-9_]{2,80}$/.test(card.noticeCode)
    )
      return null;
    return card as AssistantEvidenceCard;
  }
  if (card.kind === 'ticket_status') {
    const statuses = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED', 'UNKNOWN'] as const;
    const delivery = ['pending', 'processing', 'delivered', 'dead', 'UNKNOWN'] as const;
    if (
      !statuses.includes(card.status as (typeof statuses)[number]) ||
      typeof card.category !== 'string' ||
      !/^[A-Z][A-Z0-9_]{2,80}$/.test(card.category) ||
      typeof card.priority !== 'string' ||
      !/^[A-Z][A-Z0-9_]{2,80}$/.test(card.priority) ||
      !delivery.includes(card.notificationState as (typeof delivery)[number]) ||
      (card.updatedAt !== null &&
        (typeof card.updatedAt !== 'string' || !Number.isFinite(Date.parse(card.updatedAt))))
    )
      return null;
    return card as AssistantEvidenceCard;
  }
  return null;
}

function renderAssistantAnswer(content: string, articles: readonly HelpChatArticleLink[]) {
  const articleIndex = new Map(articles.map((article, index) => [article.id, { article, index }]));
  return content
    .split(/(\[source:[a-z0-9-]+#section-[1-9][0-9]*\]|\[ticket-status\]|\[technical-status\])/g)
    .map((part, index) => {
      const sourceId = part.match(/^\[source:([a-z0-9-]+#section-[1-9][0-9]*)\]$/)?.[1];
      if (sourceId) {
        const source = articleIndex.get(sourceId);
        return source ? (
          <a
            key={`${sourceId}-${index}`}
            href={source.article.href}
            className="whitespace-nowrap font-medium text-primary hover:underline"
            aria-label={`Source ${source.index + 1}: ${source.article.title}`}
          >
            [{source.index + 1}]
          </a>
        ) : null;
      }
      if (part === '[ticket-status]' || part === '[technical-status]') return null;
      return part;
    });
}

function AssistantEvidenceCards({ cards }: { cards: readonly AssistantEvidenceCard[] }) {
  if (cards.length === 0) return null;
  return (
    <div className="mt-2 space-y-2" aria-label="Verified assistant evidence">
      {cards.map((card, index) =>
        card.kind === 'technical_status' ? (
          <div
            key={`${card.kind}-${card.task}-${card.observedAt}-${index}`}
            className="rounded-lg border border-border bg-surface-soft px-3 py-2 font-body text-body-sm text-text-body"
          >
            <p className="font-medium text-text-heading">What we checked</p>
            <p>
              {formatEnum(card.task)} · {formatEnum(card.outcome)}
              {card.code ? ` · ${formatEnum(card.code)}` : ''}
            </p>
          </div>
        ) : card.kind === 'known_issue' ? (
          <div
            key={`${card.kind}-${card.task}-${card.noticeCode}-${index}`}
            className="rounded-lg border border-border bg-surface-soft px-3 py-2 font-body text-body-sm text-text-body"
          >
            <p className="font-medium text-text-heading">Known problem</p>
            <p>
              {formatEnum(card.task)} · {formatEnum(card.noticeCode)}
            </p>
          </div>
        ) : (
          <div
            key={`${card.kind}-${index}`}
            className="rounded-lg border border-border bg-surface-soft px-3 py-2 font-body text-body-sm text-text-body"
          >
            <p className="font-medium text-text-heading">Verified ticket status</p>
            <p>
              {formatEnum(card.status)} · {formatEnum(card.category)} ·{' '}
              {formatEnum(card.notificationState)}
            </p>
          </div>
        ),
      )}
    </div>
  );
}

const HELP_OFFER_QUESTION: Record<AssistantHelpOffer['task'], string> = {
  help_navigation: 'Help could not find what I needed. How can I get help with this screen?',
  roster_draft_save: 'My roster save failed more than once. How do I check whether it saved?',
  invitation_delivery: 'Invitation delivery needs attention. How do I check its current status?',
};

async function readAssistantStream(
  response: Response,
  onDelta: (text: string) => void,
): Promise<
  | {
      kind: 'guidance';
      binding: string;
      sources: HelpChatArticleLink[];
      draft: AssistantTicketPreview | null;
      evidence: AssistantEvidenceCard[];
    }
  | { kind: 'fallback'; reply: string; articles: HelpChatArticleLink[]; resultCount: number }
> {
  if (!response.body) throw new Error('Assistant stream unavailable');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let binding: string | null = null;
  let sources: HelpChatArticleLink[] = [];
  let draft: AssistantTicketPreview | null = null;
  const evidence: AssistantEvidenceCard[] = [];
  let fallback: { reply: string; articles: HelpChatArticleLink[]; resultCount: number } | null =
    null;
  let finalOutcome: string | null = null;
  try {
    while (true) {
      const chunk = await reader.read();
      buffer += decoder.decode(chunk.value, { stream: !chunk.done });
      let boundary = buffer.indexOf('\n\n');
      while (boundary >= 0) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const event = frame.match(/^event: ([a-z_]+)$/m)?.[1];
        const dataLine = frame.match(/^data: (.+)$/m)?.[1];
        const data = dataLine ? (JSON.parse(dataLine) as Record<string, unknown>) : null;
        if (event === 'answer_delta' && typeof data?.text === 'string') onDelta(data.text);
        if (event === 'sources' && Array.isArray(data?.sources)) {
          sources = data.sources.flatMap((source) => {
            if (!source || typeof source !== 'object') return [];
            const candidate = source as Record<string, unknown>;
            if (
              typeof candidate.id !== 'string' ||
              typeof candidate.title !== 'string' ||
              typeof candidate.href !== 'string' ||
              !/^\/help\/article\/[a-z0-9-]+$/.test(candidate.href)
            )
              return [];
            return [
              {
                id: candidate.id,
                title: candidate.title,
                href: candidate.href,
                summary: 'Help Centre source',
              },
            ];
          });
        }
        if (event === 'draft_ready' && data?.draft && typeof data.draft === 'object') {
          draft = parseAssistantTicketDraft(data.draft);
        }
        if (
          (event === 'technical_status' || event === 'known_issue' || event === 'ticket_status') &&
          data?.card &&
          typeof data.card === 'object'
        ) {
          const card = parseAssistantEvidenceCard(data.card);
          if (card) evidence.push(card);
        }
        if (
          event === 'fallback' &&
          typeof data?.reply === 'string' &&
          Array.isArray(data.articles) &&
          typeof data.resultCount === 'number'
        ) {
          fallback = {
            reply: data.reply,
            articles: data.articles.filter(
              (article): article is HelpChatArticleLink =>
                Boolean(article) &&
                typeof article === 'object' &&
                typeof article.id === 'string' &&
                typeof article.title === 'string' &&
                typeof article.summary === 'string' &&
                typeof article.href === 'string' &&
                /^\/help\/article\/[a-z0-9-]+$/.test(article.href),
            ),
            resultCount: data.resultCount,
          };
        }
        if (event === 'error') throw new Error('Assistant response incomplete');
        if (event === 'final' && data?.outcome === 'guidance' && typeof data.binding === 'string')
          binding = data.binding;
        if (event === 'final' && typeof data?.outcome === 'string') finalOutcome = data.outcome;
        boundary = buffer.indexOf('\n\n');
      }
      if (chunk.done) break;
    }
  } finally {
    reader.releaseLock();
  }
  if (finalOutcome === 'fallback' && fallback) return { kind: 'fallback', ...fallback };
  if (!binding || finalOutcome !== 'guidance') throw new Error('Assistant response incomplete');
  return { kind: 'guidance', binding, sources, draft, evidence };
}

/** Fixed ticket subject: chat text never travels in a ticket link. */
export const HELP_CHAT_TICKET_SUBJECT = 'Help Chat follow-up';

function HelpChatFollowUp({
  message,
  thread,
  currentPath,
  role,
  providerType,
  activeProgramFamily,
  providerProgramContext,
  enabledModules,
  governmentIntegrations,
  onAsk,
  isLatestAssistant,
}: {
  message: ChatMessage;
  thread: readonly ChatMessage[];
  currentPath: string | null;
  role: string | null | undefined;
  providerType: ProviderType;
  activeProgramFamily: ProgramFamily | null;
  providerProgramContext: ProviderProgramContext | undefined;
  enabledModules: string[] | null | undefined;
  governmentIntegrations: GovernmentIntegrationEnablement | undefined;
  onAsk: (question: string) => void;
  isLatestAssistant: boolean;
}) {
  const subject =
    latestUserQuestion(thread) ||
    getHelpChatQuestionForPath(
      currentPath,
      role,
      providerType,
      enabledModules,
      governmentIntegrations,
      activeProgramFamily === 'CHSP' ? 'CHSP' : null,
      providerProgramContext,
    ) ||
    '';
  const ticketEligible =
    isLatestAssistant &&
    isHelpChatTicketEligible({ content: message.content, resultCount: message.resultCount });
  const articleIds = (message.articles ?? []).map((article) => article.id);
  const matched = isLatestAssistant && (message.resultCount ?? 0) > 0;
  const followUpQuestions = matched
    ? getHelpChatFollowUpQuestions(
        currentPath,
        role,
        subject,
        providerType,
        3,
        enabledModules,
        governmentIntegrations,
        activeProgramFamily === 'CHSP' ? 'CHSP' : null,
        providerProgramContext,
      )
    : [];
  return (
    <div className="mt-1.5 space-y-1.5">
      {followUpQuestions.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {followUpQuestions.map((question) => (
            <button
              key={question}
              type="button"
              onClick={() => onAsk(question)}
              className="rounded-full border border-border bg-surface-sheet px-2.5 py-1 text-left font-body text-caption text-text-muted transition-colors hover:border-statura-green-accessible/40 hover:bg-surface-soft"
            >
              {question}
            </button>
          ))}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <a
          href="/help"
          className="inline-flex items-center gap-1 font-body text-caption text-text-muted transition-interactive duration-200 hover:text-statura-green-accessible"
        >
          <BookOpen className="h-3 w-3" />
          Browse Help Articles
        </a>
        {ticketEligible && matched ? (
          <HelpTicketQuietExits
            subject={HELP_CHAT_TICKET_SUBJECT}
            articleIds={articleIds}
            path={currentPath}
            tone="chat"
          />
        ) : null}
        {ticketEligible && !matched ? (
          <a
            href="/help/tickets/new"
            className="inline-flex items-center gap-1 font-body text-caption text-text-muted transition-interactive duration-200 hover:text-statura-green-accessible"
          >
            <LifeBuoy className="h-3 w-3" />
            Create a support ticket
          </a>
        ) : null}
      </div>
    </div>
  );
}

function getPanelHeight(): number {
  if (typeof window === 'undefined') return PANEL_MAX_HEIGHT;
  const available = Math.max(MIN_PANEL_HEIGHT, window.innerHeight - EDGE_MARGIN * 2);
  return Math.min(PANEL_MAX_HEIGHT, available, window.innerHeight * 0.8);
}

function useMobileHelpModality(): boolean {
  // Start modal until the client query is known. The widget already waits for
  // mount and stored position before rendering, so desktop never flashes modal.
  const [mobile, setMobile] = useState(true);
  useEffect(() => {
    const media =
      typeof window.matchMedia === 'function' ? window.matchMedia(MOBILE_HELP_MEDIA_QUERY) : null;
    const sync = () => setMobile(media ? media.matches : window.innerWidth < 768);
    sync();
    if (media) {
      media.addEventListener('change', sync);
      return () => media.removeEventListener('change', sync);
    }
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);
  return mobile;
}

export function HelpChatWidget({
  userName,
  activeProgramFamily,
  providerType,
  programContextRevision,
  assistantConversationEnabled = false,
  assistantProactiveEnabled = false,
}: HelpChatWidgetProps) {
  const pathname = usePathname();
  // Incident screens use the strict assistant screen; the composer says so.
  const onIncidentScreen = /^\/compliance\/(?:whs\/)?incidents(?:\/|$)/.test(pathname ?? '');
  const tenantId = useAuthStore((state) => state.user?.tenantId);
  const userRole = useAuthStore((state) => state.user?.role);
  const userId = useAuthStore((state) => state.user?.id);
  const { providerProgramContext } = useOrgLabels();
  const {
    enabledModules,
    loading: helpContextLoading,
    orgType,
    tenantContext,
  } = useHelpTenantContext(tenantId, providerType);
  const suggestionModules = helpContextLoading ? undefined : enabledModules;
  const governmentIntegrations = helpContextLoading
    ? undefined
    : tenantContext.governmentIntegrations;
  const helpProgram = helpContextLoading
    ? activeProgramFamily === 'CHSP'
      ? 'CHSP'
      : null
    : tenantContext.program;
  const [currentPath, setCurrentPath] = useState<string | null>(null);
  const captureEntry = currentPath === '/compliance/incidents/new';
  const incidentHelpPath =
    captureEntry ||
    currentPath?.startsWith('/compliance/incidents') ||
    currentPath?.startsWith('/compliance/whs/incidents');
  // Share the entry/office observers' tenant-and-actor caches without refetching
  // a value already read by the page. Help alone never enables capture.
  const incidentFlag = useQuery({
    queryKey: captureEntry
      ? ['incident-unified-web-flag', tenantId, userId]
      : ['unified-incident-office-flag', tenantId, userId, userRole],
    enabled: Boolean(incidentHelpPath && tenantId),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async () =>
      captureEntry
        ? isFeatureEnabled(UNIFIED_INCIDENT_CAPTURE_FLAG, tenantId)
        : (await getFeatureFlags(tenantId))[UNIFIED_INCIDENT_CAPTURE_FLAG] === true,
  });
  const unifiedIncidentCapture =
    incidentFlag.isSuccess && !incidentFlag.isFetching && incidentFlag.data === true;
  const careToday = useCareToday();
  const stationPinManagerRevoke = useStationPinHelpCapability(tenantId, userId);
  const suggestedQuestions = useMemo(
    () =>
      getHelpChatSuggestions(
        orgType,
        currentPath,
        userRole,
        providerType,
        suggestionModules,
        governmentIntegrations,
        helpProgram,
        {
          unifiedIncidentCapture,
          payrollRebuildOn: tenantContext.payrollRebuildOn,
          careToday: careToday.enabled,
          stationPinManagerRevoke,
          stationPinSelfService:
            !helpContextLoading && tenantContext.stationPinSelfService === true,
          agencyAccess: !helpContextLoading && tenantContext.agencyAccess === true,
        },
        providerProgramContext,
      ),
    [
      orgType,
      currentPath,
      providerType,
      suggestionModules,
      userRole,
      governmentIntegrations,
      helpProgram,
      providerProgramContext,
      unifiedIncidentCapture,
      tenantContext.payrollRebuildOn,
      careToday.enabled,
      stationPinManagerRevoke,
      helpContextLoading,
      tenantContext.stationPinSelfService,
      tenantContext.agencyAccess,
    ],
  );

  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [privacyNoticeOpen, setPrivacyNoticeOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [ticketDraft, setTicketDraft] = useState<AssistantTicketPreview | null>(null);
  const [ticketReceipt, setTicketReceipt] = useState<{
    ticketId: string;
    statusHandle: string;
    notificationState?: string;
  } | null>(null);
  const [ticketStatus, setTicketStatus] = useState<string | null>(null);
  const [ticketBusy, setTicketBusy] = useState(false);
  const [ticketNotice, setTicketNotice] = useState<string | null>(null);
  const [ticketFormOpen, setTicketFormOpen] = useState(false);
  const [ticketMode, setTicketMode] = useState(false);
  const [ticketCategory, setTicketCategory] = useState('HOW_TO');
  const [ticketExpected, setTicketExpected] = useState('');
  const [ticketObserved, setTicketObserved] = useState('');
  const [ticketAttempted, setTicketAttempted] = useState('');
  const [ticketImpact, setTicketImpact] = useState<'normal' | 'blocked'>('normal');
  const [helpOffer, setHelpOffer] = useState<AssistantHelpOffer | null>(null);
  const assistantBindingRef = useRef<string | null>(null);
  const activeRequestRef = useRef<AbortController | null>(null);
  const requestGenerationRef = useRef(0);
  const lastAssistantIndex = useMemo(
    () =>
      messages.reduce(
        (last, candidate, candidateIndex) =>
          candidate.role === 'assistant' ? candidateIndex : last,
        -1,
      ),
    [messages],
  );
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const latestAssistantRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const ticketSectionRef = useRef<HTMLElement>(null);
  const ticketObservedRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const helpOpenerRef = useRef<HTMLElement | null>(null);
  const isMobileModal = useMobileHelpModality();
  const mobileModalityRef = useRef(isMobileModal);
  mobileModalityRef.current = isMobileModal;

  // Persisted per-user position via layout_preferences ('user_pref' type).
  // Defaults to a bottom-right-ish position if no saved value yet.
  const {
    value: storedPosition,
    setValue: setStoredPosition,
    isLoaded,
  } = useUserPreferences<StoredPosition | null>('chat:position', null);

  const [position, setPosition] = useState<StoredPosition | null>(null);

  // Drag state for the open panel.
  const dragState = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Initialise position once the persisted value has loaded. If the saved
  // position is off-screen (e.g., monitor changed), fall back to default.
  useEffect(() => {
    if (!isLoaded) return;
    const candidate = storedPosition ?? getDefaultPosition();
    setPosition(clampToViewport(candidate));
  }, [isLoaded]);

  // Re-clamp on window resize so the panel never sits off-screen.
  useEffect(() => {
    const handler = () => {
      setPosition((prev) => (prev ? clampToViewport(prev) : prev));
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Re-clamp on open so a stale saved position from another viewport moves
  // before the user starts a new chat session.
  useEffect(() => {
    if (isOpen) setPosition((prev) => (prev ? clampToViewport(prev) : prev));
  }, [isOpen]);

  function clampToViewport(pos: StoredPosition): StoredPosition {
    if (typeof window === 'undefined') return pos;
    const w = getPanelWidth();
    const h = getPanelHeight();
    return {
      x: Math.max(EDGE_MARGIN, Math.min(pos.x, window.innerWidth - w - EDGE_MARGIN)),
      y: Math.max(EDGE_MARGIN, Math.min(pos.y, window.innerHeight - h - EDGE_MARGIN)),
    };
  }

  useEffect(() => {
    setMounted(true);
  }, []);

  const openPanel = useCallback(() => {
    if (!isOpen && document.activeElement instanceof HTMLElement)
      helpOpenerRef.current = document.activeElement;
    setIsOpen(true);
  }, [isOpen]);

  const helpOwnsScheduledFocus = useCallback((panel: HTMLElement) => {
    if (mobileModalityRef.current) return true;
    const active = panel.ownerDocument.activeElement;
    return (
      active === helpOpenerRef.current || (active instanceof HTMLElement && panel.contains(active))
    );
  }, []);

  const restoreDesktopOpener = useCallback(() => {
    if (mobileModalityRef.current) return;
    const panel = panelRef.current;
    const active = panel?.ownerDocument.activeElement;
    const opener = helpOpenerRef.current;
    if (
      !panel ||
      !(active instanceof HTMLElement) ||
      !panel.contains(active) ||
      !isTopRenderedLayer(panel) ||
      !opener?.isConnected ||
      opener.matches('[disabled], :disabled') ||
      !isVisibleLayer(opener)
    )
      return;
    opener.focus();
  }, []);

  const hidePanel = useCallback(() => {
    restoreDesktopOpener();
    setIsOpen(false);
  }, [restoreDesktopOpener]);

  const askedForPathRef = useRef<string | null>(null);
  const cancelActiveRequest = useCallback(() => {
    requestGenerationRef.current += 1;
    activeRequestRef.current?.abort();
    activeRequestRef.current = null;
    setLoading(false);
    setTicketBusy(false);
  }, []);

  const closePanel = useCallback(() => {
    cancelActiveRequest();
    restoreDesktopOpener();
    setIsOpen(false);
    setMessages([]);
    assistantBindingRef.current = null;
    setInput('');
    askedForPathRef.current = null;
  }, [cancelActiveRequest, restoreDesktopOpener]);

  const startNewConversation = useCallback(() => {
    cancelActiveRequest();
    setMessages([]);
    assistantBindingRef.current = null;
    setInput('');
    setTicketDraft(null);
    setTicketReceipt(null);
    setTicketStatus(null);
    setTicketNotice(null);
    setTicketFormOpen(false);
    setTicketMode(false);
    askedForPathRef.current = null;
  }, [cancelActiveRequest]);

  const enabledModulesKey = enabledModules === null ? 'all' : [...enabledModules].sort().join('|');

  useEffect(() => {
    cancelActiveRequest();
    restoreDesktopOpener();
    setMessages([]);
    assistantBindingRef.current = null;
    setInput('');
    setTicketDraft(null);
    setTicketReceipt(null);
    setTicketStatus(null);
    setTicketNotice(null);
    setTicketFormOpen(false);
    setTicketMode(false);
    setIsOpen(false);
    askedForPathRef.current = null;
  }, [
    programContextRevision,
    tenantId,
    userRole,
    enabledModulesKey,
    cancelActiveRequest,
    restoreDesktopOpener,
  ]);

  useEffect(
    () => () => {
      requestGenerationRef.current += 1;
      activeRequestRef.current?.abort();
    },
    [],
  );

  const applyHelpChatPath = useCallback((raw: unknown) => {
    const fromEvent = normalizeHelpChatPath(raw);
    const fromWindow =
      typeof window !== 'undefined' ? normalizeHelpChatPath(window.location.pathname) : null;
    setCurrentPath(fromEvent ?? fromWindow);
  }, []);

  useEffect(() => {
    applyHelpChatPath(pathname);
  }, [applyHelpChatPath, pathname]);

  useEffect(() => {
    if (!assistantProactiveEnabled || !currentPath || !tenantId) return;
    let cancelled = false;
    const readOffer = async () => {
      try {
        const response = await fetch(
          `/api/help/assistant/offer?path=${encodeURIComponent(currentPath)}`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          },
        );
        if (!response.ok || cancelled) return;
        const body = (await response.json()) as { offer?: Record<string, unknown> | null };
        const offer = body.offer;
        if (!offer) {
          setHelpOffer(null);
          return;
        }
        if (
          typeof offer.handle === 'string' &&
          /^h_[a-f0-9]{32}$/.test(offer.handle) &&
          (offer.task === 'help_navigation' ||
            offer.task === 'roster_draft_save' ||
            offer.task === 'invitation_delivery') &&
          typeof offer.message === 'string'
        ) {
          setHelpOffer({ handle: offer.handle, task: offer.task, message: offer.message });
        }
      } catch {
        // Proactive help is optional and cannot disrupt the current workflow.
      }
    };
    void readOffer();
    const interval = window.setInterval(() => void readOffer(), 30_000);
    const onFocus = () => void readOffer();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [assistantProactiveEnabled, currentPath, tenantId]);

  // Listen for "open-help-chat" custom event from help pages and the page opener
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ path?: unknown }>).detail;
      applyHelpChatPath(detail?.path);
      openPanel();
    };
    window.addEventListener('open-help-chat', handler);
    return () => window.removeEventListener('open-help-chat', handler);
  }, [applyHelpChatPath, openPanel]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.altKey || !e.shiftKey || e.ctrlKey || e.metaKey || e.key.toLowerCase() !== 'h') {
        return;
      }
      e.preventDefault();
      applyHelpChatPath(typeof window !== 'undefined' ? window.location.pathname : null);
      openPanel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [applyHelpChatPath, openPanel]);

  useModalLayer({
    open: isOpen && position !== null && isMobileModal,
    panelRef,
    rootRef: panelRef,
    preferredFocusRef: textareaRef,
    onClose: hidePanel,
    closeOnEscape: true,
    focusableSelector:
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
  });

  const desktopLayerOpen = isOpen && position !== null && !isMobileModal;
  useEffect(() => {
    const panel = panelRef.current;
    if (!desktopLayerOpen || !panel) return;
    const doc = panel.ownerDocument;
    const handleDesktopEscape = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.key !== 'Escape' ||
        !(doc.activeElement instanceof HTMLElement) ||
        !panel.contains(doc.activeElement) ||
        !isTopRenderedLayer(panel)
      )
        return;
      event.stopImmediatePropagation();
      hidePanel();
    };
    doc.addEventListener('keydown', handleDesktopEscape);
    return () => doc.removeEventListener('keydown', handleDesktopEscape);
  }, [desktopLayerOpen, hidePanel]);

  const latestMessageRole = messages.at(-1)?.role;

  // Follow each newly-added turn once, but do not chase streamed deltas or the
  // final loading-state change. Anchor a new assistant turn at its start so a
  // long answer keeps its first instruction in view while the rest arrives.
  useEffect(() => {
    if (latestMessageRole === 'assistant') {
      latestAssistantRef.current?.scrollIntoView({ block: 'start' });
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [latestMessageRole, messages.length]);

  useEffect(() => {
    if (!isOpen || !ticketMode) return;
    const frame = window.requestAnimationFrame(() => {
      if (!panelRef.current || !isTopRenderedLayer(panelRef.current)) return;
      ticketSectionRef.current?.scrollIntoView({ block: 'start' });
      if (!helpOwnsScheduledFocus(panelRef.current)) return;
      if (ticketFormOpen) ticketObservedRef.current?.focus();
      else ticketSectionRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [helpOwnsScheduledFocus, isOpen, ticketFormOpen, ticketMode, ticketDraft, ticketReceipt]);

  // Focus textarea when panel opens
  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setTimeout(() => {
      if (!panelRef.current || !isTopRenderedLayer(panelRef.current)) return;
      if (!helpOwnsScheduledFocus(panelRef.current)) return;
      textareaRef.current?.focus();
    }, 100);
    return () => window.clearTimeout(timer);
  }, [helpOwnsScheduledFocus, isMobileModal, isOpen]);

  // ─── Drag handlers ────────────────────────────────────────
  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!position) return;
      const touch = 'touches' in e ? e.touches[0] : null;
      const clientX = touch?.clientX ?? (e as MouseEvent | React.MouseEvent).clientX;
      const clientY = touch?.clientY ?? (e as MouseEvent | React.MouseEvent).clientY;
      dragState.current = {
        startX: clientX,
        startY: clientY,
        originX: position.x,
        originY: position.y,
      };
      setIsDragging(true);
    },
    [position],
  );

  useEffect(() => {
    if (!isDragging) return;

    const move = (e: MouseEvent | TouchEvent) => {
      if (!dragState.current) return;
      const touch = 'touches' in e ? e.touches[0] : null;
      const clientX = touch?.clientX ?? (e as MouseEvent | React.MouseEvent).clientX;
      const clientY = touch?.clientY ?? (e as MouseEvent | React.MouseEvent).clientY;
      const dx = clientX - dragState.current.startX;
      const dy = clientY - dragState.current.startY;
      const raw = { x: dragState.current.originX + dx, y: dragState.current.originY + dy };
      const next = clampToViewport(raw);
      setPosition(next);
    };

    const end = () => {
      setIsDragging(false);
      dragState.current = null;
      setPosition((current) => {
        if (current) setStoredPosition(current);
        return current;
      });
    };

    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    window.addEventListener('touchmove', move);
    window.addEventListener('touchend', end);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', end);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', end);
    };
  }, [isDragging, setStoredPosition]);

  const snapTo = useCallback(
    (corner: 'TL' | 'TR' | 'BL' | 'BR') => {
      if (typeof window === 'undefined') return;
      const w = getPanelWidth();
      const h = getPanelHeight();
      const positions: Record<'TL' | 'TR' | 'BL' | 'BR', StoredPosition> = {
        TL: { x: EDGE_MARGIN, y: EDGE_MARGIN },
        TR: { x: window.innerWidth - w - EDGE_MARGIN, y: EDGE_MARGIN },
        BL: { x: EDGE_MARGIN, y: window.innerHeight - h - EDGE_MARGIN },
        BR: { x: window.innerWidth - w - EDGE_MARGIN, y: window.innerHeight - h - EDGE_MARGIN },
      };
      const next = positions[corner];
      setPosition(next);
      setStoredPosition(next);
    },
    [setStoredPosition],
  );

  // Auto-resize textarea
  const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  }, []);

  const firstName = userName.split(' ')[0] || 'there';

  const prepareTicket = useCallback(async () => {
    if (ticketBusy || ticketDraft || ticketReceipt) return;
    const generation = requestGenerationRef.current;
    const stillCurrent = () => requestGenerationRef.current === generation;
    setTicketBusy(true);
    setTicketNotice(null);
    try {
      const response = await fetch('/api/help/assistant/ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'prepare',
          question: latestUserQuestion(messages),
          category: ticketCategory,
          expected: ticketExpected,
          observed: ticketObserved,
          attempted: ticketAttempted,
          impact: ticketImpact,
          programContextRevision,
        }),
      });
      if (!stillCurrent()) return;
      if (!response.ok) throw new Error('unavailable');
      const body = (await response.json()) as { draft?: unknown };
      if (!stillCurrent()) return;
      const draft = parseAssistantTicketDraft(body.draft);
      if (!draft) throw new Error('unavailable');
      setTicketDraft(draft);
      setTicketFormOpen(false);
      setTicketMode(true);
    } catch {
      if (stillCurrent())
        setTicketNotice(
          'The in-chat request is unavailable. You can still use the support ticket form.',
        );
    } finally {
      if (stillCurrent()) setTicketBusy(false);
    }
  }, [
    messages,
    programContextRevision,
    ticketBusy,
    ticketDraft,
    ticketReceipt,
    ticketCategory,
    ticketExpected,
    ticketObserved,
    ticketAttempted,
    ticketImpact,
  ]);

  const confirmTicket = useCallback(async () => {
    if (ticketBusy || !ticketDraft) return;
    const generation = requestGenerationRef.current;
    const stillCurrent = () => requestGenerationRef.current === generation;
    setTicketBusy(true);
    setTicketNotice(null);
    try {
      const response = await fetch('/api/help/assistant/ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'confirm',
          token: ticketDraft.token,
          confirmed: true,
          assistantBinding: assistantBindingRef.current,
        }),
      });
      if (!stillCurrent()) return;
      const body = (await response.json()) as {
        receipt?: { ticketId: string; statusHandle: string; notificationState?: string };
        binding?: string;
      };
      if (!stillCurrent()) return;
      if (response.ok && body.receipt?.ticketId && body.receipt.statusHandle) {
        if (typeof body.binding === 'string') assistantBindingRef.current = body.binding;
        setTicketReceipt(body.receipt);
        setTicketDraft(null);
        setTicketStatus('OPEN');
        setTicketNotice('Ticket saved. Notification delivery is tracked separately.');
      } else if (response.status === 503) {
        setTicketNotice(
          'The save result is uncertain. Retry confirmation with this same draft; it will not create a duplicate.',
        );
      } else if (response.status === 403) {
        setTicketNotice(
          'We cannot send your request from this chat right now. You can still use the support form.',
        );
        setTicketDraft(null);
      } else {
        setTicketNotice(
          'This preview is out of date. Check your message again, or use the support form.',
        );
        setTicketDraft(null);
      }
    } catch {
      if (stillCurrent())
        setTicketNotice(
          'The save result is uncertain. Retry confirmation with this same draft; it will not create a duplicate.',
        );
    } finally {
      if (stillCurrent()) setTicketBusy(false);
    }
  }, [ticketBusy, ticketDraft]);

  const refreshTicketStatus = useCallback(async () => {
    if (ticketBusy || !ticketReceipt) return;
    const generation = requestGenerationRef.current;
    const stillCurrent = () => requestGenerationRef.current === generation;
    setTicketBusy(true);
    try {
      const response = await fetch('/api/help/assistant/ticket/status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handle: ticketReceipt.statusHandle }),
      });
      if (!stillCurrent()) return;
      const body = (await response.json()) as {
        status?: { status?: string; notificationState?: string };
      };
      if (!stillCurrent()) return;
      if (!response.ok || !body.status?.status) throw new Error('unavailable');
      setTicketStatus(body.status.status);
      setTicketNotice(
        `Ticket ${body.status.status.toLowerCase().replaceAll('_', ' ')}. Notification: ${body.status.notificationState ?? 'unknown'}.`,
      );
    } catch {
      if (stillCurrent())
        setTicketNotice(
          'Status is temporarily unavailable. The saved ticket remains in your support requests.',
        );
    } finally {
      if (stillCurrent()) setTicketBusy(false);
    }
  }, [ticketBusy, ticketReceipt]);

  const recordResolution = useCallback(
    async (messageIndex: number, resolution: 'confirmed_complete' | 'not_resolved') => {
      const binding = messages[messageIndex]?.outcomeBinding;
      if (!binding) return;
      const generation = requestGenerationRef.current;
      const stillCurrent = () => requestGenerationRef.current === generation;
      setMessages((current) =>
        current.map((message, index) =>
          index === messageIndex && message.outcomeBinding === binding
            ? { ...message, resolution: 'saving' }
            : message,
        ),
      );
      try {
        const response = await fetch('/api/help/assistant/outcome', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ binding, resolution, programLens: activeProgramFamily }),
        });
        if (!stillCurrent()) return;
        if (!response.ok) throw new Error('unavailable');
        setMessages((current) =>
          current.map((message, index) =>
            index === messageIndex && message.outcomeBinding === binding
              ? { ...message, resolution }
              : message,
          ),
        );
      } catch {
        if (!stillCurrent()) return;
        setMessages((current) =>
          current.map((message, index) =>
            index === messageIndex && message.outcomeBinding === binding
              ? { ...message, resolution: undefined }
              : message,
          ),
        );
      }
    },
    [activeProgramFamily, messages],
  );

  const sendMessage = useCallback(
    async (raw?: string) => {
      const text = (typeof raw === 'string' ? raw : input).trim();
      if (!text || loading) return;

      const userMsg: ChatMessage = { role: 'user', content: text };
      const controller = new AbortController();
      activeRequestRef.current = controller;
      const generation = ++requestGenerationRef.current;
      const stillCurrent = () => requestGenerationRef.current === generation;
      const updatedMessages = [...messages, userMsg];
      setMessages(updatedMessages);
      setInput('');
      setLoading(true);

      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }

      try {
        const res = await fetch('/api/help/chat', {
          method: 'POST',
          signal: controller.signal,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            // Visual history may continue; the server trusts only its signed
            // binding and the latest question for the external assistant.
            messages: updatedMessages.slice(-HELP_CHAT_MAX_MESSAGES),
            assistant: assistantConversationEnabled,
            binding: assistantBindingRef.current,
            path:
              currentPath ??
              (typeof window !== 'undefined'
                ? normalizeHelpChatPath(window.location.pathname)
                : null),
            programLens: undefined,
            programContextRevision,
          }),
        });
        if (!stillCurrent()) return;

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Request failed');
        }

        if (res.headers.get('content-type')?.includes('text/event-stream')) {
          let answer = '';
          setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);
          try {
            const streamed = await readAssistantStream(res, (delta) => {
              if (!stillCurrent()) return;
              answer += delta;
              setMessages((prev) =>
                prev.map((item, index) =>
                  index === prev.length - 1 ? { ...item, content: answer } : item,
                ),
              );
            });
            if (!stillCurrent()) return;
            if (streamed.kind === 'guidance') {
              assistantBindingRef.current = streamed.binding;
              if (streamed.draft) {
                setTicketDraft(streamed.draft);
                setTicketMode(true);
              }
              setMessages((prev) =>
                prev.map((item, index) =>
                  index === prev.length - 1
                    ? {
                        ...item,
                        articles: streamed.sources,
                        evidence: streamed.evidence,
                        resultCount: streamed.sources.length,
                        outcomeBinding: streamed.binding,
                      }
                    : item,
                ),
              );
            } else {
              assistantBindingRef.current = null;
              setMessages((prev) =>
                prev.map((item, index) =>
                  index === prev.length - 1
                    ? {
                        ...item,
                        content: streamed.reply,
                        articles: streamed.articles,
                        resultCount: streamed.resultCount,
                      }
                    : item,
                ),
              );
            }
          } catch {
            if (!stillCurrent()) return;
            assistantBindingRef.current = null;
            setMessages((prev) =>
              prev.map((item, index) =>
                index === prev.length - 1
                  ? {
                      ...item,
                      content: `${answer}\n\nResponse incomplete. Please retry or create a support ticket.`,
                    }
                  : item,
              ),
            );
          }
          return;
        }
        assistantBindingRef.current = null;
        const data = (await res.json()) as {
          reply?: string;
          articles?: HelpChatArticleLink[];
          resultCount?: number;
        };
        if (!stillCurrent()) return;
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: data.reply ?? 'No guidance was returned. Try Help Centre search instead.',
            articles: Array.isArray(data.articles) ? data.articles : undefined,
            resultCount: typeof data.resultCount === 'number' ? data.resultCount : undefined,
          },
        ]);
      } catch (err) {
        if (!stillCurrent()) return;
        const message = err instanceof Error ? err.message : 'Request failed';
        if (message === SENSITIVE_HELP_CHAT_ERROR) {
          setMessages((prev) => {
            const withoutRejectedUser =
              prev.length > 0 && prev[prev.length - 1]?.role === 'user' ? prev.slice(0, -1) : prev;
            return [
              ...withoutRejectedUser,
              {
                role: 'assistant',
                content: SENSITIVE_HELP_CHAT_ERROR,
              },
            ];
          });
        } else {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content:
                message === 'Rate limit exceeded. Please wait a moment and try again.'
                  ? HELP_CHAT_RATE_LIMIT_REPLY
                  : HELP_CHAT_TRANSPORT_ERROR_REPLY,
            },
          ]);
        }
      } finally {
        if (stillCurrent()) {
          activeRequestRef.current = null;
          setLoading(false);
        }
      }
    },
    [activeProgramFamily, currentPath, input, loading, messages, programContextRevision],
  );

  const acknowledgeHelpOffer = useCallback(
    async (offer: AssistantHelpOffer, disposition: 'accepted' | 'dismissed') => {
      setHelpOffer((current) => (current?.handle === offer.handle ? null : current));
      try {
        await fetch('/api/help/assistant/offer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ handle: offer.handle, disposition }),
        });
      } catch {
        // The 30-minute server cooldown still prevents an alert storm when the
        // acknowledgement cannot be delivered.
      }
    },
    [],
  );

  const acceptHelpOffer = useCallback(
    async (offer: AssistantHelpOffer) => {
      openPanel();
      await acknowledgeHelpOffer(offer, 'accepted');
      await sendMessage(HELP_OFFER_QUESTION[offer.task]);
    },
    [acknowledgeHelpOffer, openPanel, sendMessage],
  );

  useEffect(() => {
    if (!isOpen || loading || messages.length > 0) return;
    const question = getHelpChatQuestionForPath(
      currentPath,
      userRole,
      providerType,
      suggestionModules,
      governmentIntegrations,
      helpProgram,
      providerProgramContext,
    );
    if (!question) return;
    if (askedForPathRef.current === currentPath) return;
    askedForPathRef.current = currentPath;
    void sendMessage(question);
  }, [
    currentPath,
    isOpen,
    loading,
    messages.length,
    providerType,
    helpProgram,
    providerProgramContext,
    sendMessage,
    suggestionModules,
    userRole,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage],
  );

  const handleClose = useCallback(() => {
    closePanel();
  }, [closePanel]);

  const handleMinimize = useCallback(() => {
    hidePanel();
  }, [hidePanel]);

  // Don't render the panel until we've read the stored position (avoids flash)
  const renderPanel = isOpen && position !== null;

  if (!mounted) return null;

  return (
    <>
      {helpOffer && !renderPanel ? (
        <aside
          role="status"
          aria-label="Help available"
          className="fixed bottom-4 right-4 z-[119] w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-control-border bg-surface-base p-4 shadow-lg"
        >
          <p className="font-body text-body-sm font-semibold text-text-heading">
            Would you like help?
          </p>
          <p className="mt-1 font-body text-body-sm text-text-body">{helpOffer.message}</p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => void acceptHelpOffer(helpOffer)}
              className="rounded-lg bg-primary px-3 py-2 font-body text-body-sm font-medium text-primary-foreground"
            >
              Open help
            </button>
            <button
              type="button"
              onClick={() => void acknowledgeHelpOffer(helpOffer, 'dismissed')}
              className="rounded-lg border border-control-border px-3 py-2 font-body text-body-sm text-text-body"
            >
              Dismiss
            </button>
          </div>
        </aside>
      ) : null}
      {/* Chat panel — full */}
      {renderPanel && position && (
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: position.y,
            left: position.x,
            width: `min(${PANEL_WIDTH}px, calc(100vw - ${EDGE_MARGIN * 2}px))`,
            maxHeight: `min(${PANEL_MAX_HEIGHT}px, calc(100vh - ${EDGE_MARGIN * 2}px))`,
            minHeight: `min(${MIN_PANEL_HEIGHT}px, calc(100vh - ${EDGE_MARGIN * 2}px))`,
            height: `min(${PANEL_MAX_HEIGHT}px, calc(100vh - ${EDGE_MARGIN * 2}px))`,
            zIndex: 120,
            userSelect: isDragging ? 'none' : 'auto',
          }}
          className="flex min-w-0 flex-col rounded-2xl border border-border bg-surface-sheet shadow-2xl"
          role={isMobileModal ? 'dialog' : 'complementary'}
          aria-modal={isMobileModal ? 'true' : undefined}
          data-help-layer=""
          aria-labelledby={HELP_CHAT_DIALOG_TITLE_ID}
          aria-describedby={HELP_CHAT_DIALOG_DESC_ID}
        >
          {/* Header — drag handle */}
          <div
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            className="flex min-w-0 cursor-grab items-center justify-between gap-3 rounded-t-2xl bg-primary px-4 py-3 active:cursor-grabbing"
          >
            <div className="flex min-w-0 items-center gap-2">
              <GripHorizontal className="h-4 w-4 text-text-subtle" aria-hidden="true" />
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-statura-green-accessible">
                <MessageCircle className="h-4 w-4 text-white" />
              </div>
              <div className="min-w-0">
                <p
                  id={HELP_CHAT_DIALOG_TITLE_ID}
                  className="truncate font-body text-body-sm font-semibold text-primary-foreground"
                >
                  Statura Help
                </p>
                <p id={HELP_CHAT_DIALOG_DESC_ID} className="sr-only">
                  Ask about how to use Statura. Do not include client or staff names, contact
                  details, health information, or credentials.
                </p>
                <p className="truncate font-body text-eyebrow uppercase tracking-[0.14em] text-text-muted">
                  Drag to move · use corner buttons to snap
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {/* Snap-to-corner controls */}
              <div className="mr-1 hidden items-center gap-0.5 sm:flex">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    snapTo('TL');
                  }}
                  className="rounded-control p-1 text-caption text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
                  aria-label="Snap to top-left"
                  title="Top-left"
                >
                  ◤
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    snapTo('TR');
                  }}
                  className="rounded-control p-1 text-caption text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
                  aria-label="Snap to top-right"
                  title="Top-right"
                >
                  ◥
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    snapTo('BL');
                  }}
                  className="rounded-control p-1 text-caption text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
                  aria-label="Snap to bottom-left"
                  title="Bottom-left"
                >
                  ◣
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    snapTo('BR');
                  }}
                  className="rounded-control p-1 text-caption text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
                  aria-label="Snap to bottom-right"
                  title="Bottom-right"
                >
                  ◢
                </button>
              </div>
              {messages.length > 0 ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    startNewConversation();
                  }}
                  className="rounded-control px-2 py-1 text-eyebrow uppercase tracking-wide text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
                  aria-label="Start a new conversation"
                  title="New conversation"
                >
                  New
                </button>
              ) : null}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleMinimize();
                }}
                className="rounded-control p-1.5 text-slate-400 transition-interactive duration-200 hover:bg-white/10 hover:text-white"
                aria-label="Hide chat"
                title="Hide chat"
              >
                <Minus className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                className="rounded-control p-1.5 text-slate-400 transition-interactive duration-200 hover:bg-white/10 hover:text-white"
                aria-label="Close chat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="min-w-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
            {!ticketMode && helpOffer ? (
              <section
                aria-label="Recent task help"
                className="rounded-xl border border-control-border bg-surface-soft p-3"
              >
                <p className="font-body text-body-sm font-semibold text-text-heading">
                  Recent task needs attention
                </p>
                <p className="mt-1 font-body text-body-sm text-text-body">{helpOffer.message}</p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => void acceptHelpOffer(helpOffer)}
                    className="rounded-lg bg-primary px-3 py-1.5 font-body text-body-sm font-medium text-primary-foreground"
                  >
                    Help me check
                  </button>
                  <button
                    type="button"
                    onClick={() => void acknowledgeHelpOffer(helpOffer, 'dismissed')}
                    className="rounded-lg border border-control-border bg-surface-sheet px-3 py-1.5 font-body text-body-sm text-text-body hover:bg-surface-soft"
                  >
                    Dismiss
                  </button>
                </div>
              </section>
            ) : null}
            {!ticketMode && messages.length === 0 && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary">
                    <MessageCircle className="h-3.5 w-3.5 text-primary-foreground" />
                  </div>
                  <div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-surface-soft px-3.5 py-2.5">
                    <p className="break-words font-body text-body-sm leading-relaxed text-text-body">
                      {`Hi${firstName !== 'there' ? ` ${firstName}` : ''}! Ask how to use this screen and I will find the relevant Help Centre steps.`}
                    </p>
                  </div>
                </div>
                <div className="pl-9">
                  <p className="mb-2 font-body text-eyebrow font-medium uppercase tracking-wide text-text-muted">
                    Try asking
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {suggestedQuestions.map((question) => (
                      <button
                        key={question}
                        type="button"
                        onClick={() => {
                          void sendMessage(question);
                        }}
                        className="rounded-full border border-border bg-surface-sheet px-3 py-1.5 text-left font-body text-caption text-text-muted transition-colors hover:border-statura-green-accessible/40 hover:bg-surface-soft"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {!ticketMode &&
              messages.map((msg, idx) => (
                <div key={idx}>
                  {msg.role === 'user' ? (
                    <div className="flex justify-end">
                      <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-primary px-3.5 py-2.5">
                        <p className="whitespace-pre-wrap break-words font-body text-body-sm leading-relaxed text-primary-foreground">
                          {msg.content}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div
                      ref={idx === lastAssistantIndex ? latestAssistantRef : undefined}
                      className="flex gap-2"
                    >
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary">
                        <MessageCircle className="h-3.5 w-3.5 text-primary-foreground" />
                      </div>
                      <div className="max-w-[80%] min-w-0">
                        <div className="rounded-2xl rounded-tl-sm bg-surface-soft px-3.5 py-2.5">
                          <p className="whitespace-pre-wrap break-words font-body text-body-sm leading-relaxed text-text-body">
                            {renderAssistantAnswer(msg.content, msg.articles ?? [])}
                          </p>
                        </div>
                        <AssistantEvidenceCards cards={msg.evidence ?? []} />
                        {msg.articles && msg.articles.length > 0 && (
                          <div className="mt-2 space-y-1.5">
                            {msg.articles.map((article) => (
                              <a
                                key={article.id}
                                href={article.href}
                                className="block rounded-lg border border-border bg-surface-sheet px-3 py-2 transition-colors hover:border-primary hover:bg-surface-soft"
                              >
                                <p className="font-body text-body-sm font-medium text-text-heading">
                                  {article.title}
                                </p>
                                <p className="mt-0.5 line-clamp-2 font-body text-body-sm text-text-muted">
                                  {article.summary}
                                </p>
                              </a>
                            ))}
                          </div>
                        )}
                        <HelpChatFollowUp
                          message={msg}
                          thread={messages}
                          currentPath={currentPath}
                          role={userRole}
                          providerType={providerType}
                          activeProgramFamily={activeProgramFamily}
                          enabledModules={suggestionModules}
                          governmentIntegrations={governmentIntegrations}
                          providerProgramContext={providerProgramContext}
                          isLatestAssistant={idx === lastAssistantIndex}
                          onAsk={(question) => {
                            void sendMessage(question);
                          }}
                        />
                        {idx === lastAssistantIndex &&
                        assistantConversationEnabled &&
                        msg.outcomeBinding &&
                        !loading ? (
                          <div
                            className="mt-2 flex flex-wrap items-center gap-2 font-body text-body-sm text-text-muted"
                            aria-label="Was this guidance useful?"
                          >
                            {msg.resolution === 'confirmed_complete' ? (
                              <span>Marked as solved.</span>
                            ) : msg.resolution === 'not_resolved' ? (
                              <span>Not resolved. Ask another question or use Get support.</span>
                            ) : (
                              <>
                                <span>Did this solve it?</span>
                                <button
                                  type="button"
                                  disabled={msg.resolution === 'saving'}
                                  onClick={() => void recordResolution(idx, 'confirmed_complete')}
                                  className="font-medium text-primary hover:underline disabled:opacity-50"
                                >
                                  Yes
                                </button>
                                <button
                                  type="button"
                                  disabled={msg.resolution === 'saving'}
                                  onClick={() => void recordResolution(idx, 'not_resolved')}
                                  className="font-medium text-primary hover:underline disabled:opacity-50"
                                >
                                  Not yet
                                </button>
                              </>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              ))}

            {!ticketMode && loading && (
              <div className="flex gap-2">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary">
                  <MessageCircle className="h-3.5 w-3.5 text-primary-foreground" />
                </div>
                <div className="rounded-2xl rounded-tl-sm bg-surface-soft px-3.5 py-2.5">
                  <Loader2 className="h-4 w-4 animate-spin text-text-subtle" />
                </div>
              </div>
            )}

            {ticketMode && (ticketFormOpen || ticketDraft || ticketReceipt || ticketNotice) && (
              <section
                ref={ticketSectionRef}
                tabIndex={-1}
                aria-label="Support request"
                className="rounded-xl border border-border bg-surface-sheet p-3 shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => setTicketMode(false)}
                  className="mb-3 font-body text-body-sm font-medium text-primary hover:underline"
                >
                  Back to chat
                </button>
                {ticketFormOpen && !ticketDraft && !ticketReceipt && (
                  <div className="space-y-2">
                    <p className="font-body text-body-sm font-semibold text-text-heading">
                      Get support
                    </p>
                    <p className="font-body text-body-sm text-text-muted">
                      Tell us what you were trying to do and what happened. Please leave out names
                      and personal details. You can check your message before sending it.
                    </p>
                    <label className="block font-body text-body-sm text-text-body">
                      What happened?
                      <textarea
                        ref={ticketObservedRef}
                        value={ticketObserved}
                        onChange={(event) => setTicketObserved(event.target.value)}
                        maxLength={350}
                        rows={2}
                        placeholder="For example: I tapped Save, but nothing happened."
                        className="mt-1 w-full resize-none rounded-lg border border-control-border bg-surface-sheet px-2 py-1.5 font-body text-body-sm text-text-heading focus:border-focus focus:outline-none focus:ring-2 focus:ring-focus"
                      />
                    </label>
                    <details>
                      <summary className="w-fit cursor-pointer font-body text-body-sm font-medium text-primary hover:underline">
                        Add details
                      </summary>
                      <div className="mt-2 space-y-2 rounded-lg bg-surface-soft p-2.5">
                        <label className="block font-body text-body-sm text-text-body">
                          Issue type
                          <select
                            value={ticketCategory}
                            onChange={(event) => setTicketCategory(event.target.value)}
                            className="mt-1 w-full rounded-lg border border-control-border bg-surface-sheet px-2 py-1.5 font-body text-body-sm text-text-heading focus:border-focus focus:outline-none focus:ring-2 focus:ring-focus"
                          >
                            <option value="HOW_TO">I need help doing something</option>
                            <option value="BUG_REPORT">Something is not working</option>
                            <option value="ACCESS_ISSUE">I cannot open something or sign in</option>
                            <option value="PLATFORM_ISSUE">The app is slow or unavailable</option>
                            <option value="OTHER">Other</option>
                          </select>
                        </label>
                        <label className="block font-body text-body-sm text-text-body">
                          What should have happened?
                          <textarea
                            value={ticketExpected}
                            onChange={(event) => setTicketExpected(event.target.value)}
                            maxLength={350}
                            rows={2}
                            className="mt-1 w-full resize-none rounded-lg border border-control-border bg-surface-sheet px-2 py-1.5 font-body text-body-sm text-text-heading focus:border-focus focus:outline-none focus:ring-2 focus:ring-focus"
                          />
                        </label>
                        <label className="block font-body text-body-sm text-text-body">
                          What have you tried so far?
                          <textarea
                            value={ticketAttempted}
                            onChange={(event) => setTicketAttempted(event.target.value)}
                            maxLength={350}
                            rows={2}
                            className="mt-1 w-full resize-none rounded-lg border border-control-border bg-surface-sheet px-2 py-1.5 font-body text-body-sm text-text-heading focus:border-focus focus:outline-none focus:ring-2 focus:ring-focus"
                          />
                        </label>
                        <label className="flex items-center gap-2 font-body text-body-sm text-text-body">
                          <input
                            type="checkbox"
                            checked={ticketImpact === 'blocked'}
                            onChange={(event) =>
                              setTicketImpact(event.target.checked ? 'blocked' : 'normal')
                            }
                          />
                          I cannot finish what I need to do
                        </label>
                      </div>
                    </details>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => void prepareTicket()}
                        disabled={ticketBusy || !ticketObserved.trim()}
                        className="rounded-lg bg-primary px-3 py-1.5 font-body text-body-sm font-medium text-primary-foreground disabled:opacity-50"
                      >
                        Preview request
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setTicketFormOpen(false);
                          setTicketMode(false);
                        }}
                        className="rounded-lg border border-control-border bg-surface-sheet px-3 py-1.5 font-body text-body-sm text-text-body hover:bg-surface-soft"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {ticketDraft && (
                  <>
                    <p className="font-body text-body-sm font-semibold text-text-heading">
                      Review your support request
                    </p>
                    <p className="mt-1 font-body text-body-sm text-text-muted">
                      Nothing has been submitted yet. Confirm only if these details are accurate.
                    </p>
                    <p className="mt-2 font-body text-body-sm font-medium text-text-heading">
                      {ticketDraft.subject}
                    </p>
                    {!ticketDraft.preview && ticketDraft.routeTarget && (
                      <p className="mt-1 font-body text-body-sm text-text-muted">
                        This will go to{' '}
                        {ticketDraft.routeTarget === 'tenant_administrators'
                          ? 'your organisation’s administrators'
                          : 'Statura support'}
                        .
                      </p>
                    )}
                    {ticketDraft.preview ? (
                      <>
                        <dl className="mt-2 space-y-1 rounded-lg bg-surface-soft p-2 font-body text-body-sm leading-snug">
                          {ticketDraft.preview.primary.map((row) => (
                            <div key={row.key} className="min-w-0">
                              <dt className="inline font-medium text-text-heading">
                                {row.label}:{' '}
                              </dt>
                              <dd className="inline whitespace-pre-wrap break-words text-text-body">
                                {row.value}
                              </dd>
                            </div>
                          ))}
                        </dl>
                        <details className="mt-2 rounded-lg border border-control-border bg-surface-sheet p-2">
                          <summary className="cursor-pointer font-body text-body-sm font-medium text-text-body">
                            Details included with this request
                          </summary>
                          <p className="mt-2 max-h-48 overflow-y-auto whitespace-pre-wrap break-words font-body text-body-sm text-text-body">
                            {ticketDraft.preview.submittedDescription}
                          </p>
                        </details>
                      </>
                    ) : (
                      <p className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap break-words rounded-lg bg-surface-soft p-2 font-body text-body-sm text-text-body">
                        {ticketDraft.description}
                      </p>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => void confirmTicket()}
                        disabled={ticketBusy}
                        className="rounded-lg bg-primary px-3 py-1.5 font-body text-body-sm font-medium text-primary-foreground disabled:opacity-50"
                      >
                        {ticketBusy ? 'Checking…' : 'Confirm and submit'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setTicketDraft(null);
                          setTicketFormOpen(true);
                        }}
                        disabled={ticketBusy}
                        className="rounded-lg border border-control-border bg-surface-sheet px-3 py-1.5 font-body text-body-sm text-text-body hover:bg-surface-soft disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
                {ticketReceipt && (
                  <>
                    <p className="font-body text-body-sm font-semibold text-text-heading">
                      Support ticket saved
                    </p>
                    <p className="mt-1 font-body text-body-sm text-text-body">
                      Status: {formatEnum(ticketStatus ?? 'checking')} · Notification:{' '}
                      {formatEnum(ticketReceipt.notificationState ?? 'pending')}
                    </p>
                    <a
                      href={`/help/tickets/${ticketReceipt.ticketId}`}
                      className="mt-2 inline-block font-body text-body-sm text-primary hover:underline"
                    >
                      View ticket and replies
                    </a>
                    <button
                      type="button"
                      onClick={() => void refreshTicketStatus()}
                      disabled={ticketBusy}
                      className="ml-2 mt-2 rounded-lg border border-control-border bg-surface-sheet px-3 py-1.5 font-body text-body-sm text-text-body hover:bg-surface-soft disabled:opacity-50"
                    >
                      Refresh status
                    </button>
                  </>
                )}
                {ticketNotice && (
                  <p role="status" className="mt-2 font-body text-body-sm text-text-body">
                    {ticketNotice}
                  </p>
                )}
                <a
                  href="/help/tickets/new"
                  className="mt-2 inline-block font-body text-body-sm text-primary hover:underline"
                >
                  Open support form
                </a>
              </section>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className={ticketMode ? 'hidden' : 'border-t border-border px-4 py-3'}>
            {!ticketMode ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setTicketObserved((current) => current || latestUserQuestion(messages));
                    if (!ticketDraft && !ticketReceipt) setTicketFormOpen(true);
                    setTicketMode(true);
                  }}
                  disabled={ticketBusy}
                  className="mb-2 inline-flex items-center gap-1 font-body text-body-sm font-medium text-primary hover:underline disabled:opacity-50"
                >
                  <LifeBuoy className="h-3.5 w-3.5" />
                  {ticketDraft || ticketReceipt ? 'View support request' : 'Get support'}
                </button>
                <div className="flex min-w-0 items-end gap-2">
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={handleTextareaChange}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      onIncidentScreen
                        ? 'Ask how to do a step on this screen'
                        : 'Ask how to use Statura'
                    }
                    aria-label={
                      onIncidentScreen
                        ? 'Ask how to do a step on this screen'
                        : 'Ask how to use Statura'
                    }
                    rows={1}
                    className="min-w-0 flex-1 resize-none rounded-xl border border-border bg-surface-soft px-3.5 py-2.5 font-body text-body-sm text-text-body placeholder:text-text-subtle focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                  />
                  <button
                    onClick={() => {
                      void sendMessage();
                    }}
                    disabled={!input.trim() || loading}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground transition-interactive duration-200 hover:opacity-95 disabled:opacity-50"
                    aria-label="Send message"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <div className="mt-2 flex items-center justify-center gap-1 font-body text-body-sm text-text-muted">
                  <span>
                    {onIncidentScreen
                      ? 'Leave out names and details of the incident.'
                      : "Don't enter personal, health or client details."}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPrivacyNoticeOpen((open) => !open)}
                    aria-expanded={privacyNoticeOpen}
                    aria-controls={privacyNoticeOpen ? 'help-chat-privacy-notice' : undefined}
                    aria-label="What not to enter and how your text is used"
                    title="What not to enter and how your text is used"
                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-text-muted hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <Info className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </div>
                {privacyNoticeOpen ? (
                  <p
                    id="help-chat-privacy-notice"
                    className="mt-1 text-center font-body text-body-sm text-text-muted"
                  >
                    Do not enter resident or client health details, personal identifiers, or
                    Services Australia, HSDP, Medicare or PRODA data. Leave out staff names, contact
                    details and credentials too. Automatic screening is best-effort and will not
                    catch everything. Text that passes screening is sent to Anthropic under its
                    commercial terms and DPA and may be processed outside Australia.{' '}
                    <a
                      href="/legal/ai-disclosure"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      How data is used
                    </a>
                  </p>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}
